import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sparkles, Target, Eye } from 'lucide-react';

export function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="about" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="about-section"
    >
      {/* Background hairline */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-line-2 to-transparent" />
      
      <div ref={ref} className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Who We Are */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 mb-32">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <p className="eyebrow section-eyebrow-line mb-6">
              Who We Are
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-stark leading-tight tracking-tight">
              Building Systems of{' '}
              <span className="text-gradient">Transformation</span>
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <p className="text-lg md:text-xl text-stark-2 font-light leading-relaxed mb-6">
              ServeNetics is a next-generation AI innovation company founded on a 
              simple but powerful belief: <span className="text-stark font-medium">Technology should serve humanity — not replace it.</span>
            </p>
            <p className="text-base text-stark-3 leading-relaxed">
              We design and deploy intelligent systems that help businesses grow faster, 
              operate smarter, and create meaningful impact — while simultaneously expanding 
              access to opportunity for underserved communities around the world. At our core, 
              we are not just building tools. We are building systems of transformation.
            </p>
          </motion.div>
        </div>
        
        {/* Mission & Vision Cards */}
        <div className="grid md:grid-cols-2 gap-px bg-line-1">
          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="bg-surface p-8 md:p-10 group hover:bg-surface-2 transition-colors duration-300 relative"
            data-testid="mission-card"
          >
            <span className="eyebrow mb-4 block">SYS.01 — Mission</span>
            <div className="w-11 h-11 rounded-md bg-ink-2 border border-line-1 flex items-center justify-center mb-5">
              <Target className="w-5 h-5 text-stark" />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-stark mb-4 tracking-tight">
              Our Mission
            </h3>
            <p className="text-stark-2 leading-relaxed">
              To become a global instrument of impact, empowering millions of lives 
              through AI-driven solutions, education, and digital transformation — 
              especially in underserved regions.
            </p>
          </motion.div>
          
          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="bg-surface p-8 md:p-10 group hover:bg-surface-2 transition-colors duration-300 relative"
            data-testid="vision-card"
          >
            <span className="eyebrow mb-4 block">SYS.02 — Vision</span>
            <div className="w-11 h-11 rounded-md bg-ink-2 border border-line-1 flex items-center justify-center mb-5">
              <Eye className="w-5 h-5 text-stark" />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-stark mb-4 tracking-tight">
              Our Vision
            </h3>
            <p className="text-stark-2 leading-relaxed mb-4">
              A world where:
            </p>
            <ul className="space-y-2.5 text-stark-2">
              <li className="flex items-start gap-3">
                <Sparkles className="w-3.5 h-3.5 text-signal mt-1.5 flex-shrink-0" />
                <span>Every business can leverage AI to scale efficiently</span>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="w-3.5 h-3.5 text-signal mt-1.5 flex-shrink-0" />
                <span>Every individual has access to digital intelligence tools</span>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="w-3.5 h-3.5 text-signal mt-1.5 flex-shrink-0" />
                <span>Innovation bridges the gap between developed and emerging economies</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AboutSection;
